import { useState } from "react";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const KINDS = { announce: "Change announcement", reminder: "7-day reminder", chase: "Read-confirmation chaser", cancel: "Cancellation", new: "New products" };
const sel = "h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm";

/** Super admin sends the real partner email to their own address, marked [PREVIEW]. */
export function PreviewEmailDialog({ d }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("announce");
  const [picked, setBatchId] = useState("");
  const batchId = d.batches.some((b) => b.batch_id === picked) ? picked : d.batches[0]?.batch_id || "";
  const [audience, setAudience] = useState("reseller");
  const [busy, setBusy] = useState(false);
  const live = d.items.filter((i) => i.status === "live").map((i) => i.item_id);
  const itemIds = d.unannounced.length ? d.unannounced : live.slice(0, 5);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/price-list/preview-email", { kind, audience, batch_id: batchId, item_ids: itemIds });
      toast.success(`Preview sent to ${data.sent_to}`); setOpen(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send the preview"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="price-preview-email-open"><Send className="h-3 w-3" /> Send preview email</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="price-preview-email-dialog">
        <DialogHeader>
          <DialogTitle>Send a preview email</DialogTitle>
          <DialogDescription>The exact partner email goes to your own address only, marked [PREVIEW]. Works while emails are off.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <label className="block space-y-1"><span className="text-xs text-slate-500">Email</span>
            <select value={kind} onChange={(e) => setKind(e.target.value)} className={sel} data-testid="price-preview-kind">
              {Object.entries(KINDS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
          {kind !== "new" ? (
            <label className="block space-y-1"><span className="text-xs text-slate-500">Change batch</span>
              <select value={batchId} onChange={(e) => setBatchId(e.target.value)} className={sel} data-testid="price-preview-batch">
                {!d.batches.length && <option value="">No batches yet</option>}
                {d.batches.map((b) => <option key={b.batch_id} value={b.batch_id}>{`${b.name} · ${b.effective_date}`}</option>)}</select></label>
          ) : <p className="text-xs text-slate-500" data-testid="price-preview-items">Uses {itemIds.length} {d.unannounced.length ? "unannounced" : "live"} item(s).</p>}
          <label className="block space-y-1"><span className="text-xs text-slate-500">As seen by partner type</span>
            <select value={audience} onChange={(e) => setAudience(e.target.value)} className={sel} data-testid="price-preview-audience">
              <option value="agent">Agent (MSRP)</option><option value="reseller">Reseller</option><option value="sp">Service Provider</option></select></label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="price-preview-cancel">Cancel</Button>
          <Button onClick={send} disabled={busy || (kind !== "new" && !batchId)} data-testid="price-preview-send">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Send to me</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
